import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import {
  Bot,
  User,
  Send,
  Sparkles,
  FileDown,
  Save,
  RefreshCw,
  Copy,
  FileText,
  Palette,
  Check,
  X,
  Download,
} from "lucide-react";
import { marked } from "marked";
import DOMPurify from "dompurify";
import html2pdf from "html2pdf.js";

/* ------------------------------------------------------------------ */
/*  Template Gallery                                                   */
/*  Each template is a set of CSS rules applied to the .mq-cv scope.   */
/*  Colors are injected via --c-primary / --c-accent CSS variables so  */
/*  users can fully customise them.                                    */
/* ------------------------------------------------------------------ */

const TEMPLATES = [
  {
    id: "classic",
    name: "Classic Serif",
    description: "Timeless Georgia serif with blue section underlines.",
    defaultPrimary: "#0033FF",
    defaultAccent: "#0a0a0a",
    preview: "serif · rule-under",
    css: (p, a) => `
      .mq-cv{font-family:Georgia,'Times New Roman',serif;color:#0a0a0a;line-height:1.55;padding:48px 56px;background:#fff;}
      .mq-cv h1{font-family:Helvetica,Arial,sans-serif;font-size:30px;letter-spacing:-0.5px;margin:0 0 4px;border-bottom:2px solid ${p};padding-bottom:8px;color:${a};}
      .mq-cv h2{font-family:Helvetica,Arial,sans-serif;font-size:14px;text-transform:uppercase;letter-spacing:2px;color:${p};margin:22px 0 10px;border-bottom:1px solid #e5e7eb;padding-bottom:4px;}
      .mq-cv h3{font-family:Helvetica,Arial,sans-serif;font-size:14px;margin:12px 0 2px;color:${a};}
      .mq-cv p,.mq-cv li{font-size:13px;}
      .mq-cv ul{margin:4px 0 12px 22px;}
      .mq-cv a{color:${p};}
    `,
  },
  {
    id: "modern",
    name: "Modern Sans",
    description: "Clean Helvetica, full-width colored header bar.",
    defaultPrimary: "#0033FF",
    defaultAccent: "#ffffff",
    preview: "sans · bar-top",
    css: (p, a) => `
      .mq-cv{font-family:Helvetica,Arial,sans-serif;color:#0a0a0a;line-height:1.55;padding:0 0 40px;background:#fff;}
      .mq-cv h1{background:${p};color:${a};padding:28px 48px;font-size:28px;letter-spacing:-0.5px;margin:0 0 24px;}
      .mq-cv h2{font-size:12px;text-transform:uppercase;letter-spacing:2.5px;color:${p};margin:20px 48px 8px;}
      .mq-cv h3{font-size:14px;margin:10px 48px 2px;color:#0a0a0a;}
      .mq-cv p,.mq-cv li{font-size:13px;margin-left:48px;margin-right:48px;}
      .mq-cv ul{margin:4px 48px 12px 70px;}
      .mq-cv hr{border:0;border-top:1px solid #eee;margin:14px 48px;}
      .mq-cv a{color:${p};}
    `,
  },
  {
    id: "minimal",
    name: "Minimal",
    description: "Editorial, lots of whitespace, monochrome.",
    defaultPrimary: "#111111",
    defaultAccent: "#555555",
    preview: "mono · airy",
    css: (p, a) => `
      .mq-cv{font-family:'Inter',Helvetica,Arial,sans-serif;color:#111;line-height:1.7;padding:64px 72px;background:#fff;}
      .mq-cv h1{font-weight:900;font-size:32px;letter-spacing:-1px;margin:0 0 12px;color:${p};}
      .mq-cv h2{font-size:10px;text-transform:uppercase;letter-spacing:3px;color:${a};margin:26px 0 8px;font-weight:700;}
      .mq-cv h3{font-size:13px;margin:8px 0 2px;font-weight:700;color:${p};}
      .mq-cv p,.mq-cv li{font-size:12.5px;color:#333;}
      .mq-cv ul{margin:4px 0 12px 18px;}
    `,
  },
  {
    id: "executive",
    name: "Executive",
    description: "Deep navy header, serif body — suits C-suite.",
    defaultPrimary: "#0b1a4a",
    defaultAccent: "#c9a04a",
    preview: "navy · gold",
    css: (p, a) => `
      .mq-cv{font-family:'Georgia',serif;color:#111;line-height:1.6;padding:48px 56px;background:#fff;border-top:8px solid ${p};}
      .mq-cv h1{font-size:30px;margin:0 0 4px;color:${p};letter-spacing:-0.5px;}
      .mq-cv h2{font-family:'Helvetica',sans-serif;font-size:13px;text-transform:uppercase;letter-spacing:3px;color:${a};margin:22px 0 10px;}
      .mq-cv h3{font-size:14px;margin:10px 0 2px;color:${p};font-weight:700;}
      .mq-cv p,.mq-cv li{font-size:13px;}
      .mq-cv ul{margin:4px 0 12px 22px;}
    `,
  },
  {
    id: "creative",
    name: "Creative",
    description: "Bold hero block with accent bar on every section.",
    defaultPrimary: "#FF3B30",
    defaultAccent: "#0a0a0a",
    preview: "bold · red",
    css: (p, a) => `
      .mq-cv{font-family:'Helvetica',Arial,sans-serif;color:${a};line-height:1.6;padding:48px 56px;background:#fff;}
      .mq-cv h1{font-size:36px;font-weight:900;letter-spacing:-1px;margin:0 0 12px;color:${a};}
      .mq-cv h1::after{content:'';display:block;width:60px;height:6px;background:${p};margin-top:10px;}
      .mq-cv h2{font-size:12px;text-transform:uppercase;letter-spacing:3px;color:${p};margin:26px 0 10px;padding-left:12px;border-left:4px solid ${p};}
      .mq-cv h3{font-size:14px;margin:8px 0 2px;font-weight:700;}
      .mq-cv p,.mq-cv li{font-size:13px;}
      .mq-cv ul{margin:4px 0 12px 22px;}
    `,
  },
  {
    id: "tech",
    name: "Tech / Monospace",
    description: "Monospace accents, perfect for engineers.",
    defaultPrimary: "#00A86B",
    defaultAccent: "#0a0a0a",
    preview: "mono · devops",
    css: (p, a) => `
      .mq-cv{font-family:'JetBrains Mono','Menlo',monospace;color:${a};line-height:1.6;padding:48px 56px;background:#fff;}
      .mq-cv h1{font-family:'Helvetica',sans-serif;font-size:28px;font-weight:900;margin:0 0 6px;color:${a};}
      .mq-cv h1::before{content:'> ';color:${p};}
      .mq-cv h2{font-size:11px;text-transform:uppercase;letter-spacing:2px;color:${p};margin:22px 0 8px;}
      .mq-cv h2::before{content:'## ';}
      .mq-cv h3{font-family:'Helvetica',sans-serif;font-size:14px;margin:10px 0 2px;font-weight:700;}
      .mq-cv p,.mq-cv li{font-size:12.5px;font-family:'Helvetica',sans-serif;}
      .mq-cv ul{margin:4px 0 12px 22px;}
    `,
  },
  {
    id: "academic",
    name: "Academic",
    description: "Traditional layout for researchers & PhDs.",
    defaultPrimary: "#3b2a1a",
    defaultAccent: "#8b6a3a",
    preview: "serif · classic",
    css: (p, a) => `
      .mq-cv{font-family:'Garamond','Times New Roman',serif;color:#111;line-height:1.55;padding:64px 72px;background:#fff;}
      .mq-cv h1{font-size:28px;text-align:center;margin:0 0 6px;color:${p};}
      .mq-cv h1 + p{text-align:center;font-style:italic;color:${a};}
      .mq-cv h2{font-size:13px;text-transform:uppercase;letter-spacing:2px;color:${p};border-bottom:1px solid ${a};margin:26px 0 10px;padding-bottom:3px;}
      .mq-cv h3{font-size:14px;margin:10px 0 2px;font-style:italic;}
      .mq-cv p,.mq-cv li{font-size:13px;}
      .mq-cv ul{margin:4px 0 12px 24px;}
    `,
  },
  {
    id: "elegant",
    name: "Elegant",
    description: "Two-tone accent lines, generous kerning.",
    defaultPrimary: "#6b2b7a",
    defaultAccent: "#111111",
    preview: "didot · plum",
    css: (p, a) => `
      .mq-cv{font-family:'Didot','Bodoni','Georgia',serif;color:${a};line-height:1.65;padding:56px 64px;background:#fff;}
      .mq-cv h1{font-weight:400;font-size:36px;letter-spacing:2px;margin:0 0 4px;color:${p};text-transform:uppercase;}
      .mq-cv h2{font-family:'Helvetica',sans-serif;font-weight:700;font-size:11px;text-transform:uppercase;letter-spacing:3px;color:${a};margin:26px 0 10px;}
      .mq-cv h2::after{content:'';display:block;width:32px;height:2px;background:${p};margin-top:6px;}
      .mq-cv h3{font-size:13px;margin:8px 0 2px;font-style:italic;color:${p};}
      .mq-cv p,.mq-cv li{font-size:13px;}
      .mq-cv ul{margin:4px 0 12px 22px;}
    `,
  },
  {
    id: "bold",
    name: "Bold Block",
    description: "Heavy headers, dense sections for sales profiles.",
    defaultPrimary: "#FF3B30",
    defaultAccent: "#111111",
    preview: "impact · punchy",
    css: (p, a) => `
      .mq-cv{font-family:'Helvetica',Arial,sans-serif;color:${a};line-height:1.55;padding:44px 52px;background:#fff;}
      .mq-cv h1{font-size:40px;font-weight:900;letter-spacing:-1.5px;margin:0 0 6px;color:${a};}
      .mq-cv h2{font-size:13px;font-weight:900;text-transform:uppercase;letter-spacing:1px;color:#fff;background:${p};padding:6px 10px;display:inline-block;margin:24px 0 10px;}
      .mq-cv h3{font-size:14px;margin:8px 0 2px;font-weight:800;}
      .mq-cv p,.mq-cv li{font-size:13px;}
      .mq-cv ul{margin:4px 0 12px 22px;}
    `,
  },
  {
    id: "swiss",
    name: "Swiss",
    description: "Strict grid, Swiss typography — timeless.",
    defaultPrimary: "#0033FF",
    defaultAccent: "#0a0a0a",
    preview: "grid · strict",
    css: (p, a) => `
      .mq-cv{font-family:'Helvetica',Arial,sans-serif;color:${a};line-height:1.5;padding:48px 56px;background:#fff;}
      .mq-cv h1{font-size:48px;font-weight:900;letter-spacing:-2px;margin:0 0 16px;color:${a};line-height:1;}
      .mq-cv h2{font-size:10px;text-transform:uppercase;letter-spacing:3px;color:${p};margin:28px 0 10px;border-top:2px solid ${a};padding-top:8px;}
      .mq-cv h3{font-size:13px;margin:8px 0 2px;font-weight:700;}
      .mq-cv p,.mq-cv li{font-size:12.5px;}
      .mq-cv ul{margin:4px 0 12px 20px;}
    `,
  },
];

const buildPreviewHTML = (cvMarkdown, template, primary, accent, footerName) => {
  // Sanitize the markdown-rendered output before injection. marked() does
  // NOT strip <script> / on-event handlers by default, so a malicious user
  // could otherwise XSS anyone previewing their CV.
  const rawHtml = marked.parse(cvMarkdown || "");
  const html = DOMPurify.sanitize(rawHtml, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form"],
    FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus", "onblur", "formaction"],
  });
  const safeFooter = DOMPurify.sanitize(footerName || "");
  const css = template.css(primary, accent);
  return `
    <style>
      ${css}
      .mq-cv strong{color:inherit;}
      .mq-cv em{color:#555;}
      .mq-cv hr{border:0;border-top:1px solid #e5e7eb;margin:14px 0;}
      .mq-cv a{text-decoration:none;}
      .mq-footer{margin-top:24px;font-size:10px;text-transform:uppercase;letter-spacing:2px;color:#888;font-family:Helvetica,Arial,sans-serif;padding:0 56px 30px;}
    </style>
    <div class="mq-cv">
      ${html}
    </div>
    <div class="mq-footer">Generated with Maqam · ${safeFooter} · ${new Date().toLocaleDateString()}</div>
  `;
};

/* ------------------------------------------------------------------ */

export default function CVBuilder() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [targetTitle, setTargetTitle] = useState("");
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [progress, setProgress] = useState(0);
  const [ready, setReady] = useState(false);
  const [cv, setCv] = useState("");
  const [generating, setGenerating] = useState(false);
  const [templateId, setTemplateId] = useState("classic");
  const [primary, setPrimary] = useState("#0033FF");
  const [accent, setAccent] = useState("#0a0a0a");
  const [showGallery, setShowGallery] = useState(false);
  const [pdfPopup, setPdfPopup] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const endRef = useRef(null);

  const template = useMemo(
    () => TEMPLATES.find((t) => t.id === templateId) || TEMPLATES[0],
    [templateId]
  );

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, thinking]);

  const start = async () => {
    setThinking(true);
    try {
      const r = await api.post("/ai/cv-builder/start", { target_title: targetTitle });
      setSession(r.data);
      setMessages([{ role: "assistant", content: r.data.question }]);
    } catch {
      toast.error("Couldn't start the CV builder");
    } finally {
      setThinking(false);
    }
  };

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || !session) return;
    const userMsg = input.trim();
    setMessages((m) => [...m, { role: "user", content: userMsg }]);
    setInput("");
    setThinking(true);
    try {
      const r = await api.post("/ai/cv-builder/reply", {
        session_id: session.session_id,
        message: userMsg,
      });
      setMessages((m) => [...m, { role: "assistant", content: r.data.question }]);
      setProgress(r.data.progress || 0);
      setReady(r.data.ready || false);
    } catch {
      toast.error("Something went wrong, try again");
    } finally {
      setThinking(false);
    }
  };

  const generate = async () => {
    if (!session) return;
    setGenerating(true);
    try {
      const r = await api.post("/ai/cv-builder/generate", {
        session_id: session.session_id,
        message: "generate",
      });
      setCv(r.data.cv);
      toast.success("CV generated — download popup opening");
      // Auto-popup the PDF dialog
      setTimeout(() => setPdfPopup(true), 400);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const saveToProfile = async () => {
    try {
      await api.post("/ai/cv-builder/save", { cv_text: cv });
      toast.success("Saved to your profile — ready for bulk apply");
    } catch {
      toast.error("Save failed");
    }
  };

  const downloadMD = () => {
    const blob = new Blob([cv], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Maqam-CV.md";
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPDF = async () => {
    if (!cv) return;
    setDownloading(true);
    try {
      const name = (user?.full_name || "Maqam").replace(/[^a-zA-Z0-9]+/g, "-");
      const container = document.createElement("div");
      container.innerHTML = buildPreviewHTML(cv, template, primary, accent, user?.full_name);
      container.style.position = "fixed";
      container.style.left = "-10000px";
      container.style.top = "0";
      container.style.width = "800px";
      document.body.appendChild(container);
      await html2pdf()
        .set({
          margin: [6, 6, 10, 6],
          filename: `${name}-CV-${template.id}.pdf`,
          html2canvas: { scale: 2, useCORS: true, letterRendering: true },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: ["avoid-all", "css", "legacy"] },
        })
        .from(container)
        .save();
      document.body.removeChild(container);
      toast.success("PDF downloaded");
      setPdfPopup(false);
    } catch {
      toast.error("PDF export failed");
    } finally {
      setDownloading(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(cv);
    toast.success("Copied");
  };

  const reset = () => {
    setSession(null);
    setMessages([]);
    setProgress(0);
    setReady(false);
    setCv("");
    setTargetTitle("");
  };

  const applyTemplate = (tpl) => {
    setTemplateId(tpl.id);
    setPrimary(tpl.defaultPrimary);
    setAccent(tpl.defaultAccent);
    setShowGallery(false);
  };

  return (
    <div className="space-y-8" data-testid="cv-builder-page">
      <div>
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">/ CV Builder</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
          No CV? No problem.
          <br />
          <span className="text-gradient">Answer a few questions. Get a CV.</span>
        </h1>
        <p className="text-gray-600 mt-3 max-w-2xl">
          Answer a few conversational questions — Maqam writes a polished, ATS-friendly CV for you, lets you
          pick from 10 pro templates with custom colors, and auto-opens a PDF download when it's ready.
        </p>
      </div>

      {!session ? (
        <div className="border border-black/10 bg-[#F8F9FA] p-8 space-y-5 max-w-2xl">
          <label className="block">
            <span className="block font-mono text-xs uppercase tracking-widest text-gray-500 mb-2">
              Target role (optional)
            </span>
            <input
              value={targetTitle}
              onChange={(e) => setTargetTitle(e.target.value)}
              placeholder="e.g. Senior Data Scientist"
              className="w-full bg-transparent border-b-2 border-gray-200 focus:border-black outline-none py-3 text-lg"
              data-testid="cvb-target-title"
            />
          </label>
          <button
            onClick={start}
            disabled={thinking}
            className="bg-black text-white hover:bg-[#0033FF] font-bold px-8 py-4 flex items-center gap-3 transition-colors disabled:opacity-60"
            data-testid="cvb-start"
          >
            <Sparkles className="w-5 h-5" />
            {thinking ? "Warming up…" : "Start the conversation"}
          </button>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-6">
          {/* Chat panel — wider, cleaner (ChatGPT-inspired) */}
          <div className="border border-black/10 bg-white flex flex-col h-[78vh] rounded-sm">
            <div className="px-6 py-4 border-b border-black/10 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Bot className="w-5 h-5 text-[#0033FF]" />
                <div>
                  <div className="font-display font-bold text-base leading-tight">CV Coach</div>
                  <div className="font-mono text-[10px] text-gray-500 uppercase tracking-widest">
                    Maqam · Claude Sonnet
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 w-[140px]">
                  <div className="flex-1 h-1.5 bg-gray-200">
                    <div
                      className="h-full bg-[#0033FF] transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <span className="font-mono text-[10px] text-gray-500">{progress}%</span>
                </div>
                <button
                  onClick={reset}
                  className="p-2 hover:bg-gray-100"
                  title="Start over"
                  data-testid="cvb-reset"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div
              className="flex-1 overflow-y-auto px-6 py-6 space-y-5 bg-[#FAFAFA]"
              data-testid="cvb-chat"
            >
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}
                >
                  <div
                    className={`w-9 h-9 flex-shrink-0 rounded-full flex items-center justify-center border ${
                      m.role === "user"
                        ? "bg-black text-white border-black"
                        : "bg-white text-[#0033FF] border-black/10"
                    }`}
                  >
                    {m.role === "user" ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                  </div>
                  <div
                    className={`max-w-[78%] px-5 py-3.5 text-[15px] leading-relaxed rounded-2xl ${
                      m.role === "user"
                        ? "bg-[#0033FF] text-white rounded-tr-sm"
                        : "bg-white text-black border border-black/5 rounded-tl-sm shadow-sm"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {thinking && (
                <div className="flex gap-3">
                  <div className="w-9 h-9 flex-shrink-0 rounded-full flex items-center justify-center border border-black/10 bg-white text-[#0033FF]">
                    <Bot className="w-4 h-4" />
                  </div>
                  <div className="px-5 py-3.5 bg-white rounded-2xl rounded-tl-sm border border-black/5 font-mono text-sm text-gray-500 flex items-center gap-1.5">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                    <span
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "0.15s" }}
                    />
                    <span
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "0.3s" }}
                    />
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>
            <form onSubmit={send} className="border-t border-black/10 p-4 bg-white">
              <div className="flex items-end gap-2 border border-black/10 focus-within:border-black rounded-2xl px-4 py-3 bg-white">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send(e);
                    }
                  }}
                  placeholder="Type your answer… (Shift+Enter for newline)"
                  disabled={thinking}
                  rows={1}
                  className="flex-1 bg-transparent outline-none resize-none text-[15px] leading-relaxed max-h-32"
                  data-testid="cvb-input"
                />
                <button
                  type="submit"
                  disabled={thinking || !input.trim()}
                  className="bg-black text-white hover:bg-[#0033FF] px-4 py-2 rounded-full disabled:opacity-40 transition-colors flex-shrink-0"
                  data-testid="cvb-send"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center justify-between mt-3 px-1">
                <div className="text-[11px] font-mono text-gray-400 uppercase tracking-widest">
                  {messages.filter((m) => m.role === "user").length} answers · {ready ? "ready to generate" : "keep going"}
                </div>
                <button
                  onClick={generate}
                  disabled={generating || messages.filter((m) => m.role === "user").length < 3}
                  className={`px-5 py-2 text-xs font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 ${
                    ready
                      ? "bg-[#FF3B30] text-white hover:bg-black"
                      : "bg-black text-white hover:bg-[#0033FF]"
                  }`}
                  data-testid="cvb-generate"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  {generating ? "Writing…" : cv ? "Regenerate" : "Generate CV"}
                </button>
              </div>
            </form>
          </div>

          {/* CV preview panel */}
          <div className="border border-black/10 bg-white flex flex-col h-[78vh] rounded-sm">
            <div className="px-5 py-3 border-b border-black/10 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="font-display font-bold text-sm">CV Preview</div>
                <button
                  onClick={() => setShowGallery(true)}
                  className="text-xs font-mono uppercase tracking-widest border border-black/10 px-3 py-1.5 hover:bg-black hover:text-white transition-colors flex items-center gap-1.5"
                  data-testid="cvb-open-gallery"
                >
                  <Palette className="w-3 h-3" /> {template.name}
                </button>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-gray-500">
                    Primary
                    <input
                      type="color"
                      value={primary}
                      onChange={(e) => setPrimary(e.target.value)}
                      className="w-6 h-6 border border-black/10 cursor-pointer"
                      data-testid="cvb-color-primary"
                    />
                  </label>
                  <label className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-gray-500">
                    Accent
                    <input
                      type="color"
                      value={accent}
                      onChange={(e) => setAccent(e.target.value)}
                      className="w-6 h-6 border border-black/10 cursor-pointer"
                      data-testid="cvb-color-accent"
                    />
                  </label>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {cv && (
                  <>
                    <button
                      onClick={copy}
                      className="p-1.5 border border-black/10 hover:bg-black hover:text-white"
                      title="Copy"
                      data-testid="cvb-copy"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={downloadMD}
                      className="p-1.5 border border-black/10 hover:bg-black hover:text-white"
                      title="Download .md"
                      data-testid="cvb-download-md"
                    >
                      <FileText className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setPdfPopup(true)}
                      className="px-2.5 py-1.5 border border-black bg-black text-white hover:bg-[#FF3B30] hover:border-[#FF3B30] flex items-center gap-1.5 text-xs font-bold"
                      title="Download PDF"
                      data-testid="cvb-download-pdf"
                    >
                      <FileDown className="w-3.5 h-3.5" /> PDF
                    </button>
                    <button
                      onClick={saveToProfile}
                      className="px-2.5 py-1.5 border border-[#34C759] bg-[#34C759] text-white hover:bg-black hover:border-black flex items-center gap-1.5 text-xs font-bold"
                      title="Save to profile"
                      data-testid="cvb-save"
                    >
                      <Save className="w-3.5 h-3.5" /> Save
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6 bg-[#F1F1F1]">
              {cv ? (
                <div
                  className="mx-auto max-w-[760px] shadow-xl"
                  data-testid="cvb-preview"
                  dangerouslySetInnerHTML={{
                    __html: buildPreviewHTML(cv, template, primary, accent, user?.full_name),
                  }}
                />
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center text-gray-500 px-6">
                  <FileDown className="w-12 h-12 mb-4 stroke-[1]" />
                  <p className="text-sm max-w-xs">
                    Keep answering the coach's questions. When you've covered enough, click{" "}
                    <span className="font-bold text-black">Generate CV</span>.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Template Gallery Modal */}
      {showGallery && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur flex items-center justify-center p-4"
          onClick={() => setShowGallery(false)}
          data-testid="cvb-gallery-modal"
        >
          <div
            className="bg-white w-full max-w-5xl max-h-[88vh] overflow-hidden flex flex-col border border-black/10 rounded-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-black/10 flex items-center justify-between">
              <div>
                <div className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">
                  / Gallery
                </div>
                <h2 className="font-display font-black text-2xl tracking-tighter">
                  Pick a template
                </h2>
              </div>
              <button
                onClick={() => setShowGallery(false)}
                className="p-2 hover:bg-black/5"
                data-testid="cvb-gallery-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto grid md:grid-cols-2 xl:grid-cols-3 gap-5">
              {TEMPLATES.map((tpl) => {
                const active = tpl.id === templateId;
                return (
                  <button
                    key={tpl.id}
                    onClick={() => applyTemplate(tpl)}
                    className={`text-left border bg-white flex flex-col hover:-translate-y-0.5 transition-transform ${
                      active ? "border-black ring-2 ring-[#0033FF]" : "border-black/10"
                    }`}
                    data-testid={`cvb-template-${tpl.id}`}
                  >
                    <div
                      className="aspect-[4/3] border-b border-black/10 overflow-hidden relative"
                      style={{
                        background:
                          "repeating-linear-gradient(45deg,#fafafa 0 8px,#fff 8px 16px)",
                      }}
                    >
                      <div
                        className="absolute inset-0 p-3 scale-[0.42] origin-top-left"
                        style={{ width: "238%", height: "238%" }}
                        dangerouslySetInnerHTML={{
                          __html: buildPreviewHTML(
                            `# ${user?.full_name || "Your Name"}\n${tpl.preview}\n\n## Experience\n### Senior Role — Company\nImpact bullet one\n\n## Skills\n- React\n- Python\n- AWS`,
                            tpl,
                            tpl.defaultPrimary,
                            tpl.defaultAccent,
                            user?.full_name
                          ),
                        }}
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-display font-bold">{tpl.name}</div>
                        {active && (
                          <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#0033FF]">
                            <Check className="w-3 h-3" /> active
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-gray-600">{tpl.description}</div>
                      <div className="flex gap-2 mt-3">
                        <span
                          className="w-4 h-4 border border-black/10"
                          style={{ background: tpl.defaultPrimary }}
                        />
                        <span
                          className="w-4 h-4 border border-black/10"
                          style={{ background: tpl.defaultAccent }}
                        />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Auto PDF download popup */}
      {pdfPopup && cv && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur flex items-center justify-center p-4"
          onClick={() => setPdfPopup(false)}
          data-testid="cvb-pdf-popup"
        >
          <div
            className="bg-white w-full max-w-md border border-black/10 p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#0033FF] text-white flex items-center justify-center">
                <Download className="w-5 h-5" />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
                  Your CV is ready
                </div>
                <div className="font-display font-black text-xl tracking-tighter">
                  Download the PDF
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Styled with <span className="font-bold text-black">{template.name}</span> ·
              colors <span style={{ color: primary }} className="font-bold">primary</span>{" "}
              &amp; <span style={{ color: accent }} className="font-bold">accent</span>.
              Change the template or colors any time from the preview toolbar.
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={downloadPDF}
                disabled={downloading}
                className="flex-1 bg-black text-white hover:bg-[#0033FF] font-bold py-3 flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                data-testid="cvb-pdf-popup-download"
              >
                <FileDown className="w-4 h-4" /> {downloading ? "Generating…" : "Download PDF"}
              </button>
              <button
                onClick={() => setPdfPopup(false)}
                className="px-5 py-3 border border-black/10 hover:bg-gray-100 text-sm"
                data-testid="cvb-pdf-popup-close"
              >
                Later
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
